import { useTranslation } from 'react-i18next'
import { useEffect, useContext, useState, ReactNode } from 'react'

import { Tabs, Tab, TextField, MenuItem } from '@mui/material'

import Dialog from '../common/dialog'
import { Pocket } from '../../utils/dexie/db'
import { FormError, SystemUser, UserSessionType } from '../../types'
import { requestAccessToken } from '../../api/auth.api'
import SystemUserDataForm from './system-user-data-form'
import { AuthContext } from '../../contexts/auth.context'
import { UserContext } from '../../contexts/user.context'
import { handleValidateSystemUserFormData } from './utils'
import { handleSyncDataToLocalDB } from '../../utils/sync-data.utils'
import { parseCountriesAllowedFromStorage } from '../../utils/countries-allowed.utils'
import { modalTabs, textFieldWhiteBg } from '../../theme/custom-styles'
import { normalizeSystemUserData } from '../../utils/normalize-data.utils'
import { NotificationsContext } from '../../contexts/notifications.context'
import { assignSystemUser, createSystemUser } from '../../api/system-users.api'

type TabPanelProps = {
  dir?: string
  index: number
  value: number
  maxHeight?: string
  children?: ReactNode
}

export const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, maxHeight, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      style={{ maxHeight: maxHeight || '200px', width: '100%', overflowY: 'auto' }}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  )
}

export const AddNewSystemUserModal = ({
  pockets,
  systemUsersData,
  handleCloseSession,
  handleOpenStateOfModal,
  handleResetPocketFilterData,
  disableSystemUserRoleSelect,
  systemUsersFromOtherCountries,
  handleAddSystemUserDataToTable
}: {
  pockets?: Array<Pocket>
  handleCloseSession: () => void
  systemUsersData: Array<SystemUser>
  disableSystemUserRoleSelect: boolean
  handleResetPocketFilterData: () => void
  systemUsersFromOtherCountries: Array<SystemUser>
  handleOpenStateOfModal: (openState: boolean) => void
  handleAddSystemUserDataToTable: (systemUserDataToAddToTable: SystemUser) => void
}) => {
  const { isAuth } = useContext(AuthContext)
  const { userSession } = useContext(UserContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t, i18n } = useTranslation(['system-users-management', 'common'])

  const [selectedResultsTab, setSelectedResultsTab] = useState(0)
  const [errors, setErrors] = useState<FormError | { [key: string]: string }>({})
  const [selectedPocketId, setSelectedPocketId] = useState<string>('')
  const [systemUserData, setSystemUserData] = useState<Partial<SystemUser>>({
    role: '',
    username: '',
    lastName: '',
    firstName: ''
  })
  const [selectedSystemUserId, setSelectedSystemUserId] = useState<string>('')
  const [dialogNewUserIsLoading, setDialogNewUserIsLoading] = useState<boolean>(false)
  const [countriesAllowedToOpearate, setCountriesAllowedToOpearate] = useState<Array<string>>(parseCountriesAllowedFromStorage)

  useEffect(() => {
    setCountriesAllowedToOpearate(parseCountriesAllowedFromStorage())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (_: unknown, selectedTabId: number) => {
    setSelectedResultsTab(selectedTabId)
    setSelectedSystemUserId('')
    setSelectedPocketId('')
  }

  const tabProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`
    }
  }

  const handleValidateNewSystemUserForm = async () => {
    setErrors({})
    const systemUserFormDataIsValid = handleValidateSystemUserFormData(systemUserData as SystemUser, systemUsersData, userSession as UserSessionType, 'create', t)

    if (!systemUserFormDataIsValid.formDataIsValid) {
      if (Array.isArray(pockets) && pockets?.length > 1 && !selectedPocketId) {
        return setErrors({ ...systemUserFormDataIsValid.formErrors, selectedPocketId: t('newSystemUserModal.systemUserDataForm.errors.emptyPocketSelection') })
      } else {
        return setErrors({ ...systemUserFormDataIsValid.formErrors })
      }
    }

    if (Array.isArray(pockets) && pockets?.length > 1 && !selectedPocketId && userSession?.role?.includes('super-admin')) {
      return setErrors({ selectedPocketId: t('newSystemUserModal.systemUserDataForm.errors.emptyPocketSelection') })
    }

    try {
      setDialogNewUserIsLoading(true)
      systemUserData.systemUserLanguage = 'en'

      if (i18n?.language.includes('es')) {
        systemUserData.systemUserLanguage = 'es'
      }

      const adminOfPocketsToSet =
        Array.isArray(pockets) && pockets?.length === 1 && userSession?.role?.includes('super-admin')
          ? [String(pockets[0].id)]
          : Array.isArray(pockets) && pockets?.length > 1 && userSession?.role?.includes('super-admin')
            ? [selectedPocketId]
            : userSession?.role.indexOf('super-admin') === -1
              ? userSession.adminOfPockets
              : []

      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      systemUserData.username = systemUserData.username?.toLowerCase().trim()
      systemUserData.lastName = systemUserData.lastName?.toUpperCase().trim()
      systemUserData.firstName = systemUserData.firstName?.toUpperCase().trim()
      systemUserData.adminOfPockets = adminOfPocketsToSet
      const createdSystemUserData = await createSystemUser(systemUserData as SystemUser, accessToken)

      const systemUserToAdd = normalizeSystemUserData(createdSystemUserData.data)
      await handleSyncDataToLocalDB('system-users', systemUserToAdd)
      handleAddSystemUserDataToTable({ ...systemUserToAdd, selected: false })

      handleOpenStateOfModal(false)
      setDialogNewUserIsLoading(false)
      setSystemUserData({
        role: '',
        username: '',
        lastName: '',
        firstName: ''
      })

      handleResetPocketFilterData()
      updateNewNotificationStatus?.(true, 'system-users-created')
    } catch (error: any) {
      console.log(error, error?.response, error?.response?.data?.message)
      console.log(error?.response?.status === 401 && error?.response?.data?.message === 'Not authorized: Exists')
      if (error?.response?.status === 401) {
        if (error?.response?.data?.message.indexOf('Expired') > -1) {
          handleCloseSession()
          setDialogNewUserIsLoading(false)
          return
        }

        if (error?.response?.data?.message.indexOf('Exists') > -1) {
          setErrors({ generalError: t('formGeneralErrors.exists', { ns: 'common' }) })
          setDialogNewUserIsLoading(false)
          return
        }

        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogNewUserIsLoading(false)
    }
  }

  const handleValidateAssignSystemUserForm = async () => {
    const errorsToSet: { [key: string]: string } = {}
    setErrors(errorsToSet)

    if (!selectedPocketId && userSession?.role?.includes('super-admin')) {
      errorsToSet['selectedPocketId'] = t('newSystemUserModal.systemUserDataForm.errors.emptyPocketSelection')
    }

    if (!selectedSystemUserId && userSession?.role?.includes('super-admin')) {
      errorsToSet['selectedSystemUserId'] = t('newSystemUserModal.systemUserDataForm.errors.emptySystemUserSelection')
    }

    if (Object.keys(errorsToSet).length > 0) {
      setErrors(errorsToSet)
      return
    }

    try {
      setDialogNewUserIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await assignSystemUser({ selectedPocketId, selectedSystemUserId }, accessToken)
      const assignedSystemUserData = systemUsersFromOtherCountries.find((systemUserData) => String(systemUserData._id) === String(selectedSystemUserId))

      const systemUserToAdd = normalizeSystemUserData(assignedSystemUserData)
      await handleSyncDataToLocalDB('system-users', systemUserToAdd)
      handleAddSystemUserDataToTable({ ...systemUserToAdd, selected: false })

      handleOpenStateOfModal(false)
      setDialogNewUserIsLoading(false)
      setSystemUserData({
        role: '',
        username: '',
        lastName: '',
        firstName: ''
      })

      handleResetPocketFilterData()
      updateNewNotificationStatus?.(true, 'system-users-created')
    } catch (error: any) {
      console.log(error, error?.response, error?.response?.data?.message)
      console.log(error?.response?.status === 401 && error?.response?.data?.message === 'Not authorized: Exists')
      if (error?.response?.status === 401) {
        if (error?.response?.data?.message.indexOf('Expired') > -1) {
          handleCloseSession()
          setDialogNewUserIsLoading(false)
          return
        }

        if (error?.response?.data?.message.indexOf('Exists') > -1) {
          setErrors({ generalError: t('formGeneralErrors.exists', { ns: 'common' }) })
          setDialogNewUserIsLoading(false)
          return
        }

        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogNewUserIsLoading(false)
    }
  }

  return (
    <Dialog
      errors={errors}
      dialogIsLoading={dialogNewUserIsLoading}
      dialogTitle={t('newSystemUserModal.title')}
      handleDialogAction={() => {
        if (userSession?.role === 'super-admin-client' && countriesAllowedToOpearate.length > 0 && selectedResultsTab === 1) {
          handleValidateAssignSystemUserForm()
          return
        }

        handleValidateNewSystemUserForm()
      }}
      actionButtonText={t('newSystemUserModal.actionButtonText')}
      handleDialogOpenState={(openState: boolean) => {
        handleOpenStateOfModal(openState)

        if (openState === false) {
          setErrors({})
          setSystemUserData({
            role: '',
            username: '',
            lastName: '',
            firstName: ''
          })
        }
      }}
    >
      {userSession?.role === 'super-admin-client' && countriesAllowedToOpearate.length > 0 ? (
        <>
          <Tabs
            centered
            sx={modalTabs}
            variant="fullWidth"
            onChange={handleChange}
            value={selectedResultsTab}
            aria-label="basic tabs example"
            TabIndicatorProps={{
              sx: {
                backgroundColor: 'rgb(46, 211, 183)'
              }
            }}
          >
            <Tab
              label={t('newSystemUserModal.tabs.titles.fromThisCountry')}
              {...tabProps(0)}
            />
            <Tab
              label={t('newSystemUserModal.tabs.titles.fromOtherCountries')}
              {...tabProps(1)}
            />
          </Tabs>
          <TabPanel
            index={0}
            maxHeight="550px"
            value={selectedResultsTab}
          >
            <div style={{ marginTop: '20px' }}>
              <SystemUserDataForm
                errors={errors}
                pockets={pockets}
                actionToDo="create"
                systemUserData={systemUserData}
                selectedPocketId={selectedPocketId}
                disableSystemUserRoleSelect={disableSystemUserRoleSelect}
                handleSetSystemUserData={(newSystemUserData: Partial<SystemUser>) =>
                  setSystemUserData((prevSystemUserData: Partial<SystemUser>) => ({
                    ...prevSystemUserData,
                    ...newSystemUserData
                  }))
                }
                handlePocketToAssignToUser={(pocketId: string) => setSelectedPocketId(pocketId)}
              />
            </div>
          </TabPanel>
          <TabPanel
            index={1}
            maxHeight="550px"
            value={selectedResultsTab}
          >
            <div style={{ marginTop: '20px' }}>
              <div className="system-user-data-form-container">
                <TextField
                  select
                  fullWidth
                  variant="standard"
                  value={selectedSystemUserId || ''}
                  onChange={(event) => setSelectedSystemUserId(event.target.value)}
                  label={t('newSystemUserModal.systemUserDataForm.fields.selectUser')}
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
                  error={!!(errors as { [key: string]: string }).selectedSystemUserId}
                  helperText={(errors as { [key: string]: string }).selectedSystemUserId}
                >
                  <MenuItem
                    disabled
                    value=""
                  >
                    {t('newSystemUserModal.systemUserDataForm.fields.userRoleSelectOptions.select')}
                  </MenuItem>
                  {systemUsersFromOtherCountries?.map((systemUserData: SystemUser) => {
                    return (
                      <MenuItem
                        key={String(systemUserData._id)}
                        value={String(systemUserData._id)}
                      >
                        {`${systemUserData.firstName?.toUpperCase()} ${systemUserData.lastName?.toUpperCase()}`}
                      </MenuItem>
                    )
                  })}
                </TextField>
                <TextField
                  select
                  fullWidth
                  variant="standard"
                  label={t('newSystemUserModal.systemUserDataForm.fields.pocketToAssign')}
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
                  error={!!errors.selectedPocketId}
                  helperText={errors.selectedPocketId}
                  value={selectedPocketId || ''}
                  onChange={(event) => setSelectedPocketId(event.target.value)}
                >
                  <MenuItem
                    disabled
                    value=""
                  >
                    {t('newSystemUserModal.systemUserDataForm.fields.userRoleSelectOptions.select')}
                  </MenuItem>
                  {pockets?.map((pocketData: Pocket) => {
                    return (
                      <MenuItem
                        key={pocketData.id}
                        value={String(pocketData.id)}
                      >
                        {pocketData.name?.toUpperCase()}
                      </MenuItem>
                    )
                  })}
                </TextField>
              </div>
            </div>
          </TabPanel>
        </>
      ) : (
        <SystemUserDataForm
          errors={errors}
          pockets={pockets}
          actionToDo="create"
          systemUserData={systemUserData}
          selectedPocketId={selectedPocketId}
          disableSystemUserRoleSelect={disableSystemUserRoleSelect}
          handleSetSystemUserData={(newSystemUserData: Partial<SystemUser>) =>
            setSystemUserData((prevSystemUserData: Partial<SystemUser>) => ({
              ...prevSystemUserData,
              ...newSystemUserData
            }))
          }
          handlePocketToAssignToUser={(pocketId: string) => setSelectedPocketId(pocketId)}
        />
      )}
    </Dialog>
  )
}
